import { Component, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { sortBy as _sortBy } from 'lodash';

import { Subject } from 'rxjs/internal/Subject';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';

import { TimeCorrectionModalComponent } from '@app/teacher/components/tasks/time-correction-modal/time-correction-modal.component';
import {
    LESSON_CANCELED,
    LESSON_COMPLETED,
    LESSON_COMPLETED_NOT_FINAL,
    LESSON_CREATED,
    LESSON_IN_WORK,
    MAIN_DOMAIN
} from '@app/app.constants';
import {
    CardInterface,
    ExExercise,
    Lesson,
    LessonPrivilegeSetting,
    LessonStatus,
    Pupil,
    Profile
} from '@df/interfaces';

import { ExExerciseService } from '@app/core/services/ex_exercise.service';
import { TaskService } from '@app/core/services/task.service';
import { PrivilegeService } from '@app/core/services/privilege.service';
import { TeacherService } from '@app/teacher/services/teacher.service';

import { BsModalService, ModalDirective } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { AccountService } from '@app/teacher/services/account.service';

@Component({
    selector: 'app-tasks-component',
    templateUrl: './tasks-component.component.html',
    styleUrls: ['./tasks-component.component.scss']
})
export class TasksComponent implements OnInit, OnDestroy {
    @Output() classObj;
    @Output() lesson: Lesson;
    @Output() isTeach = true;
    @Output() activeCard: CardInterface;
    @ViewChild(TimeCorrectionModalComponent, { static: false }) changeStatusModalComponent: TimeCorrectionModalComponent;
    @ViewChild('taskModal', { static: false }) taskModal: ModalDirective;

    headerName = 'Проведение урока';
    modalRef: BsModalRef;
    cardNumber: number = null;
    selectedStudentId: number = null;
    currentStudent: Pupil;
    errorCard = null;
    main_domain: string = MAIN_DOMAIN;
    modalMessage = '';
    students: Array<Pupil>;
    lesson_created: number = LESSON_CREATED;
    lesson_in_work: number = LESSON_IN_WORK;
    lesson_completed: number = LESSON_COMPLETED;
    lesson_canceled: number = LESSON_CANCELED;
    lesson_completed_not_final: number = LESSON_COMPLETED_NOT_FINAL;
    privilegeSettings: LessonPrivilegeSetting[] = [];
    studentHasKairosBonus: boolean;
    exExercise: ExExercise;
    selectedStudentIndex: number;
    timerActivated = false;
    availableCardId: number;
    currentLessonId: number;
    isFullScreen: boolean = false;
    profile: Profile;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private teacherService: TeacherService,
        private accountService: AccountService,
        private route: ActivatedRoute,
        private exExerciseService: ExExerciseService,
        private taskService: TaskService,
        private privSvc: PrivilegeService,
        private modalService: BsModalService
    ) {}

    public ngOnInit(): void {
        this.init();

        this.accountService.getProfileData().subscribe(response => {
            if (response.data && response.data.user.username) {
                this.profile = response.data;
            }
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    getClass(classObj): void {
        this.classObj = classObj;
        this.students = classObj.students;
    }

    clearCardNumber(): void {
        this.cardNumber = null;
        this.activeCard = null;
        this.getAvailableCardIdForExExercise();
    }

    firstCheckCard(): void {
        if (this.lesson.status === LESSON_CREATED) {
            this.changeLessonStatus(LESSON_CREATED);
        }
    }

    findCard(): void {
        if (this.cardNumber !== null) {
            this.taskService
                .getCardToNumber(this.cardNumber, this.lesson.id, this.selectedStudentId)
                .subscribe(resp => {
                    if (resp.success) {
                        this.activeCard = resp.data;
                        this.activeCard.exercises = _sortBy(this.activeCard.exercises, 'exercise_order');
                        this.checkForKairosBonus();
                    }
                });
        }
    }

    checkForKairosBonus() {
        this.privSvc
            .checkForKairosBonus(this.selectedStudentId, this.activeCard.id)
            .subscribe((hasKairosBonus: boolean) => {
                this.studentHasKairosBonus = hasKairosBonus;
            });
    }

    selectStudent(student_id: number): void {
        this.clearCardNumber();
        this.selectedStudentId = student_id;
        this.currentStudent = this.students.filter(student => student.id === student_id)[0];
        this.teacherService.setCurrentStudent = this.currentStudent;
        if (this.selectedStudentId) {
            this.getAvailableCardIdForExExercise();
        }
    }

    checkFindCardPossibility(): boolean {
        return this.selectedStudentId !== null && this.cardNumber !== null;
    }

    changeLessonStatus(status: number, event?: any) {
        if (event) {
            event.stopPropagation();
        }
        this.errorCard = null;
        if (status === LESSON_CREATED) {
            this.checkDuplicateLessonsInWork().subscribe(data => {
                if (data.count > 0) {
                    this.errorCard = 'У данного класса есть не завершенные уроки. Просьба завершить.';
                } else {
                    this.lesson.status = LESSON_IN_WORK;
                    if (this.lesson) {
                        this.updateLessonStatus(this.lesson).subscribe();
                    }
                }
            });
        } else if (status === LESSON_IN_WORK) {
            // если пользователь пытается закрыть урок который ещё в работе
            // вызываю модальное окно с подтверждением о закрытии урока, для того
            // чтобы предотвратить случайное закрытие урока
            this.changeStatusModalComponent.showModal();
        }
        return false;
    }

    /*
     * Метод используется для обработки данных после работы с модальном окном для корректировки времени
     *
     * @param {Object} lessonForUpdate: Объект который хранит новые изменения для обновления данных урока.
     */
    changeLessonWithTimeCorrection(lessonForUpdate: { correctionData: any; hasTimeCorrectionChanges: boolean }): void {
        const correctionData = lessonForUpdate.correctionData;
        const hasTimeCorrectionChanges = lessonForUpdate.hasTimeCorrectionChanges;

        if (hasTimeCorrectionChanges) {
            this.finishWithTimeCorrection(correctionData);
        } else {
            this.updateLessonStatus(this.lesson).subscribe();
        }
    }

    clearInput(): void {
        this.cardNumber = null;
    }

    hideModal(): void {
        this.clearInput();
        this.taskModal.hide();
    }

    showModal(): void {
        this.taskModal.show();
    }

    checkDuplicateLessonsInWork(): Observable<any> {
        return this.teacherService.getClassLessonsByStatus(LessonStatus.LESSON_IN_WORK, this.classObj.id);
    }

    updateLessonStatus(lesson: Lesson): Observable<any> {
        return this.teacherService.updateLessons(String(lesson.id), lesson).pipe(
            map(lessonData => {
                this.lesson.status = lessonData.status;
                this.lesson.start_time = lessonData.start_time;
            })
        );
    }

    finishWithTimeCorrection(correctionData: any): void {
        // посылаем запрос, меняем сперва статус урока, затем меняем остальные данные
        // чтобы урок был в актуальном состоянии
        this.teacherService.finishLessonWithTimeCorrection(this.lesson.id, correctionData).subscribe();
    }

    keyupHandler(): void {
        this.findCard();
    }

    private init(): void {
        this.route.parent.params.subscribe(params => {
            const class_id = params['class_id'];
            if (!!class_id) {
                this.teacherService.getClass(class_id).subscribe(classObj => this.getClass(classObj));
            }
            this.route.params.subscribe(params => {
                this.currentLessonId = params['lesson_id'];
                this.getLessonExExercise(this.currentLessonId);
                this.taskService.getLesson(this.currentLessonId).subscribe(resp => {
                    if (resp) {
                        this.lesson = resp;
                        this.getLessonPrivilegeSettings();
                    }
                });

                this.taskService.pollingPupilGet(this.currentLessonId, 4000).subscribe((response: any) => {
                    if (this.lesson.status !== response.status) {
                        this.lesson = response;
                    }
                });
            });
        });

        this.route.queryParams.subscribe((queryParams: Params) => {
            if (queryParams) {
                this.selectedStudentIndex = queryParams['student'];
            }
        });
    }

    private getLessonPrivilegeSettings(): void {
        this.privSvc.getLessonPrivilegeSettings(this.lesson.id).subscribe(response => {
            if (response.data.length > 0) {
                this.privilegeSettings = response.data;
            }
        });
    }

    private getLessonExExercise(lessonId: number) {
        this.exExerciseService.getLessonExExercise(lessonId).subscribe(resp => {
            this.exExercise = resp;
        });
    }

    showExExerciseModal(template: TemplateRef<any>, props = {}) {
        if (this.selectedStudentId) {
            this.taskService
                .getAvailableCardIdForExExercise(this.currentLessonId, this.selectedStudentId)
                .subscribe(resp => {
                    this.availableCardId = resp;
                    if (resp) {
                        this.modalRef = this.modalService.show(template, Object.assign({}, props));
                    }
                });
        }
    }

    closeModal() {
        this.modalRef.hide();
    }

    private getAvailableCardIdForExExercise() {
        this.taskService
            .getAvailableCardIdForExExercise(this.currentLessonId, this.selectedStudentId)
            .subscribe(resp => {
                this.availableCardId = resp;
            });
    }
}
